/**
 * PermitRevisionPanel — Phase 1D
 * ─────────────────────────────────────────────────────────────────────
 * Structured revision-management surface for the Permit Detail drawer.
 *
 * - Header: severity, reference #, reviewer, department, deadline, owner
 * - Body: structured per-item checklist (toggle resolved)
 * - Footer: progress + readiness badge (derived, never persisted)
 * - Add-item form for converting city feedback into structured work
 * - Metadata-edit form for opening / updating the request
 *
 * Pure presentation + form glue. All mutations route through the
 * `revisionRequest.ts` helpers via the supplied callbacks.
 */
import { useMemo, useState } from "react";
import type { Job } from "@/data/types";
import {
  REVISION_SEVERITIES,
  REVISION_SEVERITY_LABEL,
  resolveRevisionReadiness,
  revisionItemInputSchema,
  revisionRequestMetadataSchema,
  type RevisionItemInput,
  type RevisionRequestMetadataInput,
  type RevisionSeverity,
} from "@/data/permitting/revisionRequest";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle, CheckCircle2, Clock, FileWarning, Plus,
  Pencil, User as UserIcon, Calendar, Building2, Hash,
} from "lucide-react";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { usePermissions } from "@/hooks/usePermissions";
import { formatShortDate } from "@/lib/dateFormat";

interface Props {
  job: Job;
  onSetMetadata: (jobId: string, input: RevisionRequestMetadataInput) => void;
  onAddItem: (jobId: string, input: RevisionItemInput) => void;
  onToggleItem: (jobId: string, itemId: string, resolved: boolean, resolvedBy: string) => void;
}

// ── Tone helpers ───────────────────────────────────────────────────

const SEVERITY_BADGE: Record<RevisionSeverity, string> = {
  minor: "bg-muted text-muted-foreground border-border",
  standard: "bg-warning/15 text-warning border-warning/30",
  critical: "bg-destructive/15 text-destructive border-destructive/40",
};

function resolveLoggedBy(repName: string | undefined, role: string | undefined): string {
  if (repName && repName.trim()) return repName.trim();
  if (role && role.trim()) return role.trim();
  return "Unknown Operator";
}

// ── Sub-form: open/edit metadata ───────────────────────────────────

function MetadataForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: Partial<RevisionRequestMetadataInput>;
  onSubmit: (input: RevisionRequestMetadataInput) => void;
  onCancel: () => void;
}) {
  const todayIso = new Date().toISOString();
  const [refNum, setRefNum] = useState(initial?.municipalityReferenceNumber ?? "");
  const [reviewer, setReviewer] = useState(initial?.reviewerName ?? "");
  const [department, setDepartment] = useState(initial?.reviewerDepartment ?? "");
  const [received, setReceived] = useState(
    initial?.receivedDate ? initial.receivedDate.slice(0, 10) : todayIso.slice(0, 10),
  );
  const [deadline, setDeadline] = useState(
    initial?.responseDeadline ? initial.responseDeadline.slice(0, 10) : "",
  );
  const [severity, setSeverity] = useState<RevisionSeverity>(initial?.severity ?? "standard");
  const [owner, setOwner] = useState(initial?.assignedOwner ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    const isoReceived = received ? new Date(`${received}T12:00:00Z`).toISOString() : "";
    const isoDeadline = deadline ? new Date(`${deadline}T12:00:00Z`).toISOString() : undefined;
    const candidate: RevisionRequestMetadataInput = {
      municipalityReferenceNumber: refNum || undefined,
      reviewerName: reviewer || undefined,
      reviewerDepartment: department || undefined,
      receivedDate: isoReceived,
      responseDeadline: isoDeadline,
      severity,
      assignedOwner: owner || undefined,
      notes: notes || undefined,
    };
    const parsed = revisionRequestMetadataSchema.safeParse(candidate);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid revision metadata");
      return;
    }
    onSubmit(parsed.data);
  };

  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px]">Severity *</Label>
          <Select value={severity} onValueChange={(v) => setSeverity(v as RevisionSeverity)}>
            <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REVISION_SEVERITIES.map((s) => (
                <SelectItem key={s} value={s} className="text-xs">{REVISION_SEVERITY_LABEL[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Received *</Label>
          <Input
            type="date"
            value={received}
            onChange={(e) => setReceived(e.target.value)}
            className="h-8 text-xs mt-1"
          />
        </div>
        <div>
          <Label className="text-[11px]">Response deadline</Label>
          <Input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="h-8 text-xs mt-1"
          />
        </div>
        <div>
          <Label className="text-[11px]">Municipality reference #</Label>
          <Input
            value={refNum}
            onChange={(e) => setRefNum(e.target.value)}
            placeholder="e.g. PLN-2026-04812"
            className="h-8 text-xs mt-1"
          />
        </div>
        <div>
          <Label className="text-[11px]">Reviewer name</Label>
          <Input
            value={reviewer}
            onChange={(e) => setReviewer(e.target.value)}
            className="h-8 text-xs mt-1"
          />
        </div>
        <div>
          <Label className="text-[11px]">Department</Label>
          <Input
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            placeholder="e.g. Building, Zoning"
            className="h-8 text-xs mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px]">Assigned owner</Label>
          <Input
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Coordinator responsible for resubmission"
            className="h-8 text-xs mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px]">Internal notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="text-xs mt-1 resize-none"
          />
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 text-xs">Cancel</Button>
        <Button size="sm" onClick={submit} className="h-7 text-xs">Save revision packet</Button>
      </div>
    </div>
  );
}

// ── Sub-form: add item ─────────────────────────────────────────────

function AddItemForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (input: RevisionItemInput) => void;
  onCancel: () => void;
}) {
  const [docType, setDocType] = useState("");
  const [issue, setIssue] = useState("");
  const [action, setAction] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    const parsed = revisionItemInputSchema.safeParse({
      documentType: docType,
      issueDescription: issue,
      requiredAction: action,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid item");
      return;
    }
    onSubmit(parsed.data);
    setDocType(""); setIssue(""); setAction("");
  };

  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px]">Document type *</Label>
          <Input
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            placeholder="Site plan, energy calc…"
            className="h-8 text-xs mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px]">Issue description *</Label>
          <Input
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            placeholder="What did the city flag?"
            className="h-8 text-xs mt-1"
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-[11px]">Required action *</Label>
          <Textarea
            value={action}
            onChange={(e) => setAction(e.target.value)}
            rows={2}
            placeholder="What must we do to satisfy this?"
            className="text-xs mt-1 resize-none"
          />
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 text-xs">Cancel</Button>
        <Button size="sm" onClick={submit} className="h-7 text-xs">Add item</Button>
      </div>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────

export function PermitRevisionPanel({ job, onSetMetadata, onAddItem, onToggleItem }: Props) {
  const { rep } = useCurrentRep();
  const { role } = usePermissions();
  const loggedBy = useMemo(() => resolveLoggedBy(rep?.name, role), [rep, role]);

  const request = job.permitDetail?.revisionRequest ?? null;
  const readiness = useMemo(() => resolveRevisionReadiness(request), [request]);

  const isRevisionStage = job.permitDetail?.detailedStage === "Revision Requested";
  const showPanel = isRevisionStage || !!request;

  const [showMetaForm, setShowMetaForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);

  if (!showPanel) return null;

  // Empty: revision stage but nothing structured yet.
  if (!request) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <FileWarning className="h-4 w-4 text-destructive" />
          <h4 className="text-sm font-semibold text-destructive">Revision Management</h4>
        </div>
        <p className="text-xs text-muted-foreground">
          Revision requested — structured details not entered yet.
        </p>
        {showMetaForm ? (
          <MetadataForm
            onSubmit={(input) => { onSetMetadata(job.id, input); setShowMetaForm(false); }}
            onCancel={() => setShowMetaForm(false)}
          />
        ) : (
          <Button size="sm" onClick={() => setShowMetaForm(true)} className="h-7 text-xs gap-1">
            <Plus className="h-3 w-3" /> Open structured revision
          </Button>
        )}
      </div>
    );
  }

  const badgeTone =
    readiness.tone === "green" ? "bg-success/15 text-success border-success/30" :
    readiness.tone === "red" ? "bg-destructive/15 text-destructive border-destructive/40" :
    readiness.tone === "amber" ? "bg-warning/15 text-warning border-warning/30" :
    "bg-muted text-muted-foreground border-border";

  return (
    <div className="rounded-lg border p-4 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-foreground">Revision Management</h4>
            <Badge variant="outline" className={SEVERITY_BADGE[request.severity]}>
              {REVISION_SEVERITY_LABEL[request.severity]}
            </Badge>
            {request.municipalityReferenceNumber && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums">
                <Hash className="h-3 w-3" />{request.municipalityReferenceNumber}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            {request.reviewerName && (
              <span className="flex items-center gap-1">
                <UserIcon className="h-3 w-3" />{request.reviewerName}
              </span>
            )}
            {request.reviewerDepartment && (
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />{request.reviewerDepartment}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />Received {formatShortDate(request.receivedDate)}
            </span>
            {request.responseDeadline && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />Deadline {formatShortDate(request.responseDeadline)}
              </span>
            )}
            {request.assignedOwner && (
              <span className="flex items-center gap-1">
                <UserIcon className="h-3 w-3" />Owner: {request.assignedOwner}
              </span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          onClick={() => setShowMetaForm((v) => !v)}
        >
          <Pencil className="h-3 w-3" /> Edit
        </Button>
      </div>

      {showMetaForm && (
        <MetadataForm
          initial={request}
          onSubmit={(input) => { onSetMetadata(job.id, input); setShowMetaForm(false); }}
          onCancel={() => setShowMetaForm(false)}
        />
      )}

      {request.notes && (
        <p className="text-xs text-muted-foreground bg-muted/30 rounded-md p-2 border">
          {request.notes}
        </p>
      )}

      {/* Items */}
      <div className="space-y-2">
        {request.items.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No revision items logged</p>
        )}
        {request.items.map((item) => (
          <div
            key={item.id}
            className={`rounded-md border p-3 transition-colors ${
              item.resolved ? "bg-success/[0.04] border-success/30" : "bg-card border-border"
            }`}
          >
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => onToggleItem(job.id, item.id, !item.resolved, loggedBy)}
                className={`mt-0.5 shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  item.resolved
                    ? "bg-success border-success"
                    : "border-muted-foreground/40 hover:border-primary"
                }`}
                aria-pressed={item.resolved}
                aria-label={item.resolved ? "Mark unresolved" : "Mark resolved"}
              >
                {item.resolved && <CheckCircle2 className="h-3 w-3 text-success-foreground" />}
              </button>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-foreground">{item.documentType}</span>
                  {item.resolved && (
                    <span className="text-[10px] text-success">Resolved</span>
                  )}
                </div>
                <p className="text-xs text-foreground">{item.issueDescription}</p>
                <p className="text-[11px] text-muted-foreground">
                  <span className="font-medium text-foreground/80">Action:</span> {item.requiredAction}
                </p>
                {item.resolved && item.resolvedAt && (
                  <p className="text-[10px] text-muted-foreground">
                    Resolved {formatShortDate(item.resolvedAt)}
                    {item.resolvedBy ? ` · ${item.resolvedBy}` : ""}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add item */}
      {showItemForm ? (
        <AddItemForm
          onSubmit={(input) => { onAddItem(job.id, input); setShowItemForm(false); }}
          onCancel={() => setShowItemForm(false)}
        />
      ) : (
        <Button size="sm" variant="outline" onClick={() => setShowItemForm(true)} className="h-7 text-xs gap-1">
          <Plus className="h-3 w-3" /> Add revision item
        </Button>
      )}

      {/* Footer summary */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t">
        <span className="text-xs tabular-nums text-muted-foreground">
          {readiness.progressLabel}
          {readiness.unresolvedCriticalCount > 0 && (
            <span className="text-destructive ml-2 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {readiness.unresolvedCriticalCount} critical unresolved
            </span>
          )}
        </span>
        <Badge variant="outline" className={badgeTone}>
          {readiness.badgeLabel}
        </Badge>
      </div>
    </div>
  );
}
